import React, { useState, useMemo, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, CalendarDaysIcon, MapPinIcon } from '@heroicons/react/24/outline';
import {
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    addDays,
    format,
    isSameMonth,
    isSameDay,
    addMonths,
    subMonths,
    parseISO
} from 'date-fns';
import { tr } from 'date-fns/locale';

const SalesCallCalendar = ({
    salesCalls = [],
    onCallClick,
    onDateSelect,
    title = 'Road Planner Calendar',
    subtitle = 'Ziyaret planlarını ve geçmiş görüşmeleri takvim üzerinde görüntüleyin.',
    itemLabel = 'Kayıt',
    showHeaderText = true,
}) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());

    // Selection state
    const [isDragging, setIsDragging] = useState(false);
    const [selectionStart, setSelectionStart] = useState(null);
    const [selectionEnd, setSelectionEnd] = useState(null);

    const goToPreviousMonth = () => setCurrentMonth(prev => subMonths(prev, 1));
    const goToNextMonth = () => setCurrentMonth(prev => addMonths(prev, 1));
    const goToToday = () => {
        const today = new Date();
        setCurrentMonth(today);
        setSelectedDate(today);
    };

    const calendarDays = useMemo(() => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(currentMonth);
        const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
        const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

        const days = [];
        let day = startDate;

        while (day <= endDate) {
            days.push(day);
            day = addDays(day, 1);
        }

        return days;
    }, [currentMonth]);

    const getCallsForDate = (date) => {
        return salesCalls.filter(call => {
            const callDate = call.start_date ? parseISO(call.start_date) : new Date(call.created_at);
            return isSameDay(callDate, date);
        });
    };

    const getCallColorClass = (status) => {
        const normalized = String(status || '').toLowerCase();

        if (['ready', 'completed', 'tamamlandı', 'hazır'].includes(normalized)) {
            return 'bg-green-50/90 dark:bg-green-900/20 border-green-500 text-green-700 dark:text-green-300';
        }
        if (['in_progress', 'yapılıyor'].includes(normalized)) {
            return 'bg-yellow-50/90 dark:bg-yellow-900/20 border-yellow-500 text-yellow-700 dark:text-yellow-300';
        }
        if (['pending', 'waiting', 'planlandı', 'beklemede'].includes(normalized)) {
            return 'bg-orange-50/90 dark:bg-orange-900/20 border-orange-500 text-orange-700 dark:text-orange-300';
        }
        if (['cancelled', 'iptal edildi'].includes(normalized)) {
            return 'bg-red-50/90 dark:bg-red-900/20 border-red-500 text-red-700 dark:text-red-300';
        }

        return 'bg-blue-50/90 dark:bg-blue-900/20 border-blue-500 text-blue-700 dark:text-blue-300';
    };

    // Selection handlers
    const handleMouseDown = (day, e) => {
        // Prevent triggering if clicked on a call item
        if (e.target.closest('.call-item')) return;

        e.preventDefault(); // Prevent text selection
        setIsDragging(true);
        setSelectionStart(day);
        setSelectionEnd(day);
    };

    const handleMouseEnter = (day) => {
        if (isDragging) {
            setSelectionEnd(day);
        }
    };

    const handleMouseUp = () => {
        if (isDragging && selectionStart && selectionEnd) {
            setIsDragging(false);

            // Determine start and end (user might drag backwards)
            const start = selectionStart < selectionEnd ? selectionStart : selectionEnd;
            const end = selectionStart < selectionEnd ? selectionEnd : selectionStart;

            if (onDateSelect) {
                onDateSelect(start, end);
            }

            // Clear selection visual immediately or keep it until modal opens?
            // Clearing it feels smoother as the modal will take focus
            setSelectionStart(null);
            setSelectionEnd(null);
        }
    };

    useEffect(() => {
        const handleGlobalMouseUp = () => {
            if (isDragging) {
                handleMouseUp();
            }
        };

        window.addEventListener('mouseup', handleGlobalMouseUp);
        return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }, [isDragging, selectionStart, selectionEnd]);

    const isDateSelected = (day) => {
        if (!selectionStart || !selectionEnd) return false;
        const start = selectionStart < selectionEnd ? selectionStart : selectionEnd;
        const end = selectionStart < selectionEnd ? selectionEnd : selectionStart;
        return day >= start && day <= end;
    };

    const weekDays = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm animate-fadeIn select-none">
            {/* Header */}
            <div className={`flex items-center p-4 border-b border-gray-200 dark:border-gray-700 ${showHeaderText ? 'justify-between' : 'justify-end'}`}>
                {showHeaderText && (
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <CalendarDaysIcon className="w-6 h-6 text-blue-600" />
                            {title}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {subtitle}
                        </p>
                    </div>
                )}

                <div className="flex items-center space-x-4">
                    <button
                        onClick={goToToday}
                        className="px-4 py-2 text-sm font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                    >
                        Bugün
                    </button>

                    <div className="flex items-center bg-gray-50 dark:bg-gray-700 rounded-lg p-1">
                        <button
                            onClick={goToPreviousMonth}
                            className="p-2 hover:bg-white dark:hover:bg-gray-600 rounded-md shadow-sm transition-all"
                        >
                            <ChevronLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        </button>
                        <span className="w-40 text-center font-semibold text-gray-900 dark:text-white">
                            {format(currentMonth, 'MMMM yyyy', { locale: tr })}
                        </span>
                        <button
                            onClick={goToNextMonth}
                            className="p-2 hover:bg-white dark:hover:bg-gray-600 rounded-md shadow-sm transition-all"
                        >
                            <ChevronRightIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Week Days */}
            <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                {weekDays.map(day => (
                    <div key={day} className="py-3 text-center text-sm font-semibold text-gray-600 dark:text-gray-400">
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 auto-rows-fr bg-gray-200 dark:bg-gray-700 gap-[1px]">
                {calendarDays.map((day, index) => {
                    const isCurrentMonth = isSameMonth(day, currentMonth);
                    const isToday = isSameDay(day, new Date());
                    const dayCalls = getCallsForDate(day);
                    const isSelected = isDateSelected(day);

                    return (
                        <div
                            key={day.toISOString()}
                            className={`min-h-[140px] bg-white dark:bg-gray-800 p-2 transition-colors relative select-none
                ${!isCurrentMonth ? 'bg-gray-50/50 dark:bg-gray-800/50 text-gray-400' : ''}
                ${isSelected ? 'bg-blue-100 dark:bg-blue-900/40 ring-2 ring-inset ring-blue-500 z-10' : 'hover:bg-gray-50 dark:hover:bg-gray-750'}
              `}
                            onMouseDown={(e) => handleMouseDown(day, e)}
                            onMouseEnter={() => handleMouseEnter(day)}
                        >
                            <div className="flex justify-between items-start mb-2 pointer-events-none">
                                <span className={`
                  w-7 h-7 flex items-center justify-center rounded-full text-sm font-medium
                  ${isToday ? 'bg-blue-600 text-white' : 'text-gray-700 dark:text-gray-300'}
                `}>
                                    {format(day, 'd')}
                                </span>
                                {dayCalls.length > 0 && (
                                    <span className="text-xs font-medium text-gray-400">
                                        {dayCalls.length} {itemLabel}
                                    </span>
                                )}
                            </div>

                            <div className="space-y-1.5 overflow-y-auto max-h-[100px] custom-scrollbar">
                                {dayCalls.map((call) => (
                                    <div
                                        key={call.id}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onCallClick && onCallClick(call);
                                        }}
                                        className={`call-item
                      group relative p-1.5 rounded text-xs border-l-2 cursor-pointer transition-all hover:shadow-md
                      ${getCallColorClass(call.call_status)}
                    `}
                                    >
                                        <div className="font-semibold truncate pr-2">{call.agency}</div>
                                        <div className="flex items-center gap-1 text-[10px] opacity-80 truncate">
                                            <MapPinIcon className="w-3 h-3" />
                                            {call.city || call.country || 'Konum yok'}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SalesCallCalendar;
